// Shell-level player UI state — which panel is open, collapse, connection/eligibility,
// last error. Ephemeral (not in redux-persist whitelist), so it never touches localStorage.
// The actual PLAYBACK lives in the MusicKit singleton; components read it live via
// usePlayerSnapshot(). Kept in Redux so the Music page and the shell player banner (different
// subtrees) share panel + connection state across route changes.
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { MusicAuthorizationState } from '../lib/types';

export type PlayerPanel = 'none' | 'nowplaying' | 'queue' | 'lyrics';

export interface PlayerUiState {
  authState: MusicAuthorizationState;
  panel: PlayerPanel;
  collapsed: boolean;
  volume: number;
  lastError: string | null;
  autoScrollLyrics: boolean;
}

const initialState: PlayerUiState = {
  authState: 'not_connected',
  panel: 'none',
  collapsed: false,
  volume: 1,
  lastError: null,
  autoScrollLyrics: true,
};

const playerSlice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    setAuthState: (s, a: PayloadAction<MusicAuthorizationState>) => { s.authState = a.payload; },
    openPanel: (s, a: PayloadAction<PlayerPanel>) => { s.panel = a.payload; },
    togglePanel: (s, a: PayloadAction<Exclude<PlayerPanel, 'none'>>) => { s.panel = s.panel === a.payload ? 'none' : a.payload; },
    setCollapsed: (s, a: PayloadAction<boolean>) => { s.collapsed = a.payload; },
    setVolume: (s, a: PayloadAction<number>) => { s.volume = a.payload; },
    setError: (s, a: PayloadAction<string | null>) => { s.lastError = a.payload; },
    setAutoScroll: (s, a: PayloadAction<boolean>) => { s.autoScrollLyrics = a.payload; },
  },
});

export const { setAuthState, openPanel, togglePanel, setCollapsed, setVolume, setError, setAutoScroll } = playerSlice.actions;
export default playerSlice.reducer;
